import React, { useState, useEffect, useMemo } from 'react';
import { Input, Dropdown, Icon } from 'semantic-ui-react';
import styled from 'styled-components';

interface CronValidationResult {
  valid: boolean;
  error?: string;
  plainEnglish?: string;
}

interface CronExpressionInputProps {
  value: string | null | undefined;
  onChange: (value: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  timezone?: string; // Organization timezone (e.g., "America/New_York")
  showAdvancedModes?: boolean;
}

type ViewMode = 'simple' | 'visual' | 'text';
type ReminderPreset = 'daily' | 'every_other_day';

// Helper to format timezone abbreviation
const formatTimezoneAbbreviation = (timezone?: string): string => {
  if (!timezone) return 'UTC';

  try {
    // Ask the runtime for a short timezone name/code for the provided IANA timezone.
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    }).formatToParts(new Date());

    const tzPart = parts.find((part) => part.type === 'timeZoneName')?.value?.trim();
    if (!tzPart) return 'UTC';

    // Typical values are code-like (EST, EDT, UTC, GMT-4). Keep only that code token.
    const candidate = tzPart.split(' ').pop() || tzPart;
    return candidate;
  } catch {
    return 'UTC';
  }
};

// Validate cron expression format
export const validateCronExpression = (expr: string, timezone?: string): CronValidationResult => {
  if (!expr || expr.trim() === '') {
    return { valid: false, error: 'Cron expression is required' };
  }

  const parts = expr.trim().split(/\s+/);
  
  if (parts.length !== 5) {
    return { 
      valid: false, 
      error: 'Cron expression must have exactly 5 parts: minute hour day_of_month month_of_year day_of_week' 
    };
  }

  const [minute, hour, dayOfMonth, monthOfYear, dayOfWeek] = parts;

  // Validate minute (0-59 or *)
  if (!isValidCronField(minute, 0, 59)) {
    return { valid: false, error: 'Invalid minute field (must be 0-59, *, or a valid expression)' };
  }

  // Validate hour (0-23 or *)
  if (!isValidCronField(hour, 0, 23)) {
    return { valid: false, error: 'Invalid hour field (must be 0-23, *, or a valid expression)' };
  }

  // Validate day of month (1-31 or *)
  if (!isValidCronField(dayOfMonth, 1, 31)) {
    return { valid: false, error: 'Invalid day of month field (must be 1-31, *, or a valid expression)' };
  }

  // Validate month (1-12 or *)
  if (!isValidCronField(monthOfYear, 1, 12)) {
    return { valid: false, error: 'Invalid month field (must be 1-12, *, or a valid expression)' };
  }

  // Validate day of week (0-7 or *)
  if (!isValidCronField(dayOfWeek, 0, 7)) {
    return { valid: false, error: 'Invalid day of week field (must be 0-7, *, or a valid expression)' };
  }

  // If valid, generate plain English description
  const plainEnglish = convertCronToPlainEnglish(parts, timezone);

  return { valid: true, plainEnglish };
};

const isValidCronField = (field: string, min: number, max: number): boolean => {
  // Allow wildcard
  if (field === '*') return true;

  // Allow ranges (e.g., 1-5)
  if (field.includes('-')) {
    const [start, end] = field.split('-').map(Number);
    return !isNaN(start) && !isNaN(end) && start >= min && end <= max && start <= end;
  }

  // Allow lists (e.g., 1,3,5)
  if (field.includes(',')) {
    const values = field.split(',').map(Number);
    return values.every(v => !isNaN(v) && v >= min && v <= max);
  }

  // Allow step values (e.g., */5)
  if (field.includes('/')) {
    const [range, step] = field.split('/');
    const stepNum = Number(step);
    if (isNaN(stepNum) || stepNum < 1) return false;
    if (range === '*') return true;
    const rangeNum = Number(range);
    return !isNaN(rangeNum) && rangeNum >= min && rangeNum <= max;
  }

  // Allow single number
  const num = Number(field);
  return !isNaN(num) && num >= min && num <= max;
};

const convertCronToPlainEnglish = (parts: string[], timezone?: string): string => {
  const [minute, hour, dayOfMonth, monthOfYear, dayOfWeek] = parts;
  const tzAbbr = formatTimezoneAbbreviation(timezone);
  
  const formatTime = (h: string, m: string): string => {
    // Handle wildcards
    if (h === '*' && m === '*') return 'every minute';
    if (h === '*') {
      if (m.includes('/')) {
        const step = m.split('/')[1];
        return `every ${step} minutes`;
      }
      return `at minute ${m} of every hour`;
    }
    if (m === '*') {
      if (h.includes('/')) {
        const step = h.split('/')[1];
        return `every ${step} hours`;
      }
      return `every minute during hour ${h}`;
    }
    
    // Handle step values
    if (h.includes('/') || m.includes('/')) {
      if (h.includes('/') && m.includes('/')) {
        return `every ${h.split('/')[1]} hours and ${m.split('/')[1]} minutes`;
      }
      if (h.includes('/')) {
        return `every ${h.split('/')[1]} hours at minute ${m}`;
      }
      return `every ${m.split('/')[1]} minutes during hour ${h}`;
    }
    
    // Format specific time
    const hNum = parseInt(h);
    const mNum = parseInt(m);
    const period = hNum >= 12 ? 'PM' : 'AM';
    const displayHour = hNum === 0 ? 12 : hNum > 12 ? hNum - 12 : hNum;
    const displayMinute = mNum.toString().padStart(2, '0');
    return `${displayHour}:${displayMinute} ${period} ${tzAbbr}`;
  };

  const formatDayOfWeek = (dow: string): string => {
    if (dow === '*') return '';
    
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    
    // Handle step values
    if (dow.includes('/')) {
      const [range, step] = dow.split('/');
      if (range === '*') {
        return `every ${step} days`;
      }
      return `every ${step} days starting from day ${range}`;
    }
    
    // Handle lists
    if (dow.includes(',')) {
      const days = dow.split(',').map(d => {
        const dayNum = parseInt(d.trim());
        return dayNames[dayNum] || `Day ${dayNum}`;
      });
      return `on ${days.join(', ')}`;
    }
    
    // Handle ranges
    if (dow.includes('-')) {
      const [start, end] = dow.split('-').map(d => parseInt(d.trim()));
      return `from ${dayNames[start]} to ${dayNames[end]}`;
    }
    
    // Single day
    const dayNum = parseInt(dow);
    return `on ${dayNames[dayNum] || `Day ${dayNum}`}`;
  };

  const formatDayOfMonth = (dom: string): string => {
    if (dom === '*') return 'every day';
    
    // Handle step values
    if (dom.includes('/')) {
      const [range, step] = dom.split('/');
      if (range === '*') {
        return `every ${step} days`;
      }
      return `every ${step} days starting from day ${range}`;
    }
    
    // Handle lists
    if (dom.includes(',')) {
      const days = dom.split(',').map(d => d.trim());
      return `on day ${days.join(', ')} of the month`;
    }
    
    // Handle ranges
    if (dom.includes('-')) {
      return `from day ${dom.replace('-', ' to ')} of the month`;
    }
    
    // Single day
    return `on day ${dom} of the month`;
  };

  const formatMonth = (month: string): string => {
    if (month === '*') return '';
    
    const monthNames = ['', 'January', 'February', 'March', 'April', 'May', 'June', 
                       'July', 'August', 'September', 'October', 'November', 'December'];
    
    // Handle step values
    if (month.includes('/')) {
      const [range, step] = month.split('/');
      if (range === '*') {
        return `every ${step} months`;
      }
      return `every ${step} months starting from ${monthNames[parseInt(range)]}`;
    }
    
    // Handle lists
    if (month.includes(',')) {
      const months = month.split(',').map(m => {
        const mNum = parseInt(m.trim());
        return monthNames[mNum] || `Month ${mNum}`;
      });
      return `in ${months.join(', ')}`;
    }
    
    // Handle ranges
    if (month.includes('-')) {
      const [start, end] = month.split('-').map(m => parseInt(m.trim()));
      return `from ${monthNames[start]} to ${monthNames[end]}`;
    }
    
    // Single month
    const mNum = parseInt(month);
    return `in ${monthNames[mNum] || `Month ${mNum}`}`;
  };

  const timeStr = formatTime(hour, minute);
  const dayOfWeekStr = formatDayOfWeek(dayOfWeek);
  const dayOfMonthStr = formatDayOfMonth(dayOfMonth);
  const monthStr = formatMonth(monthOfYear);

  // Build the description
  const partsList: string[] = [];
  
  // Add frequency/time
  partsList.push(timeStr);
  
  // Add day specification (prefer day of week over day of month if both are specified)
  if (dayOfWeekStr && dayOfMonthStr && dayOfWeekStr !== '') {
    // If both are specified, use day of week (more common)
    partsList.push(dayOfWeekStr);
  } else if (dayOfWeekStr) {
    partsList.push(dayOfWeekStr);
  } else if (dayOfMonthStr) {
    partsList.push(dayOfMonthStr);
  }
  
  // Add month specification
  if (monthStr) {
    partsList.push(monthStr);
  }

  return partsList.join(' ');
};

// Helper function to parse cron expression into parts
const parseCronExpression = (expr: string | null | undefined) => {
  if (!expr) return { minute: '0', hour: '9', dayOfMonth: '*', month: '*', dayOfWeek: '*' };
  const parts = expr.trim().split(/\s+/);
  if (parts.length !== 5) {
    return { minute: '0', hour: '9', dayOfMonth: '*', month: '*', dayOfWeek: '*' };
  }
  return {
    minute: parts[0],
    hour: parts[1],
    dayOfMonth: parts[2],
    month: parts[3],
    dayOfWeek: parts[4],
  };
};

// Helper function to build cron expression from parts
const buildCronExpression = (parts: {
  minute: string;
  hour: string;
  dayOfMonth: string;
  month: string;
  dayOfWeek: string;
}): string => `${parts.minute} ${parts.hour} ${parts.dayOfMonth} ${parts.month} ${parts.dayOfWeek}`;

const parseSimplePreset = (expr: string | null | undefined): {
  preset: ReminderPreset;
  hour: string;
  minute: string;
} | null => {
  const parts = parseCronExpression(expr);
  const { minute, hour, dayOfMonth, month, dayOfWeek } = parts;
  const numericMinute = /^\d+$/.test(minute);
  const numericHour = /^\d+$/.test(hour);

  if (!numericMinute || !numericHour || month !== '*' || dayOfWeek !== '*') {
    return null;
  }

  if (dayOfMonth === '*') {
    return { preset: 'daily', hour, minute };
  }

  if (dayOfMonth === '*/2') {
    return { preset: 'every_other_day', hour, minute };
  }

  return null;
};

const buildSimpleCronExpression = (preset: ReminderPreset, hour: string, minute: string): string => {
  const dayOfMonth = preset === 'daily' ? '*' : '*/2';
  return `${minute} ${hour} ${dayOfMonth} * *`;
};

export const CronExpressionInput: React.FC<CronExpressionInputProps> = ({
  value,
  onChange,
  placeholder = '0 9 * * 1,3,5',
  disabled = false,
  required = false,
  timezone,
  showAdvancedModes = true,
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>('simple');
  
  // Use value if provided, otherwise use placeholder
  const effectiveValue = value || placeholder;
  const [inputValue, setInputValue] = useState(effectiveValue);
  const [validation, setValidation] = useState<CronValidationResult>({ valid: true });
  
  // Parse the current value into parts for the visual builder
  const cronParts = parseCronExpression(effectiveValue);
  const [minute, setMinute] = useState(cronParts.minute);
  const [hour, setHour] = useState(cronParts.hour);
  const [dayOfMonth, setDayOfMonth] = useState(cronParts.dayOfMonth);
  const [month, setMonth] = useState(cronParts.month);
  const [dayOfWeek, setDayOfWeek] = useState(cronParts.dayOfWeek);
  const [simplePreset, setSimplePreset] = useState<ReminderPreset>('every_other_day');
  const [simpleHour, setSimpleHour] = useState('9');
  const [simpleMinute, setSimpleMinute] = useState('0');
  const [initialized, setInitialized] = useState(false);

  // Initialize with default value if value is empty - call onChange immediately on mount
  useEffect(() => {
    if (!value && !initialized && placeholder) {
      // Immediately set the placeholder value to parent
      onChange(placeholder);
      setInputValue(placeholder);
      setInitialized(true);
    }
  }, [value, placeholder, onChange, initialized]);

  // Sync with prop changes
  useEffect(() => {
    if (value) {
      const newValue = value;
      setInputValue(newValue);
      const parts = parseCronExpression(newValue);
      setMinute(parts.minute);
      setHour(parts.hour);
      setDayOfMonth(parts.dayOfMonth);
      setMonth(parts.month);
      setDayOfWeek(parts.dayOfWeek);

      const simpleSettings = parseSimplePreset(newValue);
      if (simpleSettings) {
        setSimplePreset(simpleSettings.preset);
        setSimpleHour(simpleSettings.hour);
        setSimpleMinute(simpleSettings.minute);
      }
    }
  }, [value]);

  useEffect(() => {
    if (showAdvancedModes) return;
    if (viewMode !== 'simple') {
      setViewMode('simple');
    }
  }, [showAdvancedModes, viewMode]);

  useEffect(() => {
    if (inputValue.trim() === '') {
      if (required) {
        setValidation({ valid: false, error: 'Cron expression is required' });
      } else {
        setValidation({ valid: true });
      }
    } else {
      setValidation(validateCronExpression(inputValue, timezone));
    }
  }, [inputValue, required, timezone]);

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange(newValue.trim() || null);
  };

  const handleVisualChange = (field: 'minute' | 'hour' | 'dayOfMonth' | 'month' | 'dayOfWeek', newValue: string) => {
    const parts = {
      minute,
      hour,
      dayOfMonth,
      month,
      dayOfWeek,
      [field]: newValue,
    };
    
    // Update the specific field
    switch (field) {
      case 'minute': setMinute(newValue); break;
      case 'hour': setHour(newValue); break;
      case 'dayOfMonth': setDayOfMonth(newValue); break;
      case 'month': setMonth(newValue); break;
      case 'dayOfWeek': setDayOfWeek(newValue); break;
    }
    
    const cronExpr = buildCronExpression(parts);
    setInputValue(cronExpr);
    onChange(cronExpr);
  };

  const handleSimpleChange = (next: Partial<{ preset: ReminderPreset; hour: string; minute: string }>) => {
    const nextPreset = next.preset ?? simplePreset;
    const nextHour = next.hour ?? simpleHour;
    const nextMinute = next.minute ?? simpleMinute;

    if (next.preset) setSimplePreset(nextPreset);
    if (next.hour) setSimpleHour(nextHour);
    if (next.minute) setSimpleMinute(nextMinute);

    const cronExpr = buildSimpleCronExpression(nextPreset, nextHour, nextMinute);
    setInputValue(cronExpr);
    onChange(cronExpr);

    const parts = parseCronExpression(cronExpr);
    setMinute(parts.minute);
    setHour(parts.hour);
    setDayOfMonth(parts.dayOfMonth);
    setMonth(parts.month);
    setDayOfWeek(parts.dayOfWeek);
  };

  // Options for dropdowns - memoized to prevent recreation on every render
  const minuteOptions = useMemo(() => [
    { key: '*', value: '*', text: 'Every minute' },
    { key: '0', value: '0', text: '00' },
    { key: '15', value: '15', text: '15' },
    { key: '30', value: '30', text: '30' },
    { key: '45', value: '45', text: '45' },
    ...Array.from({ length: 60 }, (_, i) => ({ key: i.toString(), value: i.toString(), text: i.toString().padStart(2, '0') })),
  ], []);

  const hourOptions = useMemo(() => [
    { key: '*', value: '*', text: 'Every hour' },
    ...Array.from({ length: 24 }, (_, i) => {
      const period = i < 12 ? 'AM' : 'PM';
      const displayHour = i === 0 ? 12 : i > 12 ? i - 12 : i;
      return { key: i.toString(), value: i.toString(), text: `${displayHour}:00 ${period}` };
    }),
  ], []);

  const dayOfMonthOptions = useMemo(() => [
    { key: '*', value: '*', text: 'Every day' },
    ...Array.from({ length: 31 }, (_, i) => ({
      key: (i + 1).toString(),
      value: (i + 1).toString(),
      text: `Day ${i + 1}`,
    })),
  ], []);

  const monthOptions = useMemo(() => [
    { key: '*', value: '*', text: 'Every month' },
    { key: '1', value: '1', text: 'January' },
    { key: '2', value: '2', text: 'February' },
    { key: '3', value: '3', text: 'March' },
    { key: '4', value: '4', text: 'April' },
    { key: '5', value: '5', text: 'May' },
    { key: '6', value: '6', text: 'June' },
    { key: '7', value: '7', text: 'July' },
    { key: '8', value: '8', text: 'August' },
    { key: '9', value: '9', text: 'September' },
    { key: '10', value: '10', text: 'October' },
    { key: '11', value: '11', text: 'November' },
    { key: '12', value: '12', text: 'December' },
  ], []);

  const dayOfWeekOptions = useMemo(() => [
    { key: '*', value: '*', text: 'Every day' },
    { key: '0', value: '0', text: 'Sunday' },
    { key: '1', value: '1', text: 'Monday' },
    { key: '2', value: '2', text: 'Tuesday' },
    { key: '3', value: '3', text: 'Wednesday' },
    { key: '4', value: '4', text: 'Thursday' },
    { key: '5', value: '5', text: 'Friday' },
    { key: '6', value: '6', text: 'Saturday' },
    { key: '1,2,3,4,5', value: '1,2,3,4,5', text: 'Weekdays (Mon-Fri)' },
    { key: '0,6', value: '0,6', text: 'Weekends (Sat-Sun)' },
  ], []);

  const simplePresetOptions = useMemo(
    () => [
      { key: 'daily', value: 'daily', text: 'Every Day' },
      { key: 'every_other_day', value: 'every_other_day', text: 'Every Other Day' },
    ],
    []
  );

  const simpleTimeOptions = useMemo(
    () =>
      Array.from({ length: 24 }, (_, i) => {
        const period = i < 12 ? 'AM' : 'PM';
        const displayHour = i === 0 ? 12 : i > 12 ? i - 12 : i;
        return {
          key: `${i}`,
          value: `${i}`,
          text: `${displayHour}:00 ${period}`,
        };
      }),
    []
  );

  return (
    <Container>
      {showAdvancedModes && (
        <ViewModeToggle>
          <ToggleButton
            active={viewMode === 'simple'}
            onClick={() => setViewMode('simple')}
            disabled={disabled}
          >
            <Icon name="clock outline" /> Simple
          </ToggleButton>
          <ToggleButton
            active={viewMode === 'visual'}
            onClick={() => setViewMode('visual')}
            disabled={disabled}
          >
            <Icon name="calendar" /> Visual Builder
          </ToggleButton>
          <ToggleButton
            active={viewMode === 'text'}
            onClick={() => setViewMode('text')}
            disabled={disabled}
          >
            <Icon name="code" /> Text Mode
          </ToggleButton>
        </ViewModeToggle>
      )}

      {viewMode === 'simple' ? (
        <SimpleBuilder>
          <BuilderRow>
            <BuilderLabel>Frequency</BuilderLabel>
            <StyledDropdown
              placeholder="Frequency"
              selection
              options={simplePresetOptions}
              value={simplePreset}
              onChange={
                (_: React.SyntheticEvent<HTMLElement>, { value }: { value: string | number | boolean | (string | number | boolean)[] | undefined }) =>
                  handleSimpleChange({ preset: value as ReminderPreset })
              }
              disabled={disabled}
            />
          </BuilderRow>

          <BuilderRow>
            <BuilderLabel>Time</BuilderLabel>
            <StyledDropdown
              placeholder="Time"
              selection
              search
              options={simpleTimeOptions}
              value={simpleHour}
              onChange={
                (_: React.SyntheticEvent<HTMLElement>, { value }: { value: string | number | boolean | (string | number | boolean)[] | undefined }) =>
                  handleSimpleChange({ hour: String(value), minute: '0' })
              }
              disabled={disabled}
            />
          </BuilderRow>
        </SimpleBuilder>
      ) : viewMode === 'visual' ? (
        <VisualBuilder>
          <BuilderRow>
            <BuilderLabel>Minute</BuilderLabel>
            <StyledDropdown
              placeholder="Minute"
              selection
              search
              options={minuteOptions}
              value={minute}
              onChange={(_: React.SyntheticEvent<HTMLElement>, { value }: { value: string | number | boolean | (string | number | boolean)[] | undefined }) => handleVisualChange('minute', value as string)}
              disabled={disabled}
            />
          </BuilderRow>
          
          <BuilderRow>
            <BuilderLabel>Hour</BuilderLabel>
            <StyledDropdown
              placeholder="Hour"
              selection
              search
              options={hourOptions}
              value={hour}
              onChange={(_: React.SyntheticEvent<HTMLElement>, { value }: { value: string | number | boolean | (string | number | boolean)[] | undefined }) => handleVisualChange('hour', value as string)}
              disabled={disabled}
            />
          </BuilderRow>
          
          <BuilderRow>
            <BuilderLabel>Day of Month</BuilderLabel>
            <StyledDropdown
              placeholder="Day of Month"
              selection
              search
              options={dayOfMonthOptions}
              value={dayOfMonth}
              onChange={(_: React.SyntheticEvent<HTMLElement>, { value }: { value: string | number | boolean | (string | number | boolean)[] | undefined }) => handleVisualChange('dayOfMonth', value as string)}
              disabled={disabled}
            />
          </BuilderRow>
          
          <BuilderRow>
            <BuilderLabel>Month</BuilderLabel>
            <StyledDropdown
              placeholder="Month"
              selection
              search
              options={monthOptions}
              value={month}
              onChange={(_: React.SyntheticEvent<HTMLElement>, { value }: { value: string | number | boolean | (string | number | boolean)[] | undefined }) => handleVisualChange('month', value as string)}
              disabled={disabled}
            />
          </BuilderRow>
          
          <BuilderRow>
            <BuilderLabel>Day of Week</BuilderLabel>
            <StyledDropdown
              placeholder="Day of Week"
              selection
              search
              options={dayOfWeekOptions}
              value={dayOfWeek}
              onChange={(_: React.SyntheticEvent<HTMLElement>, { value }: { value: string | number | boolean | (string | number | boolean)[] | undefined }) => handleVisualChange('dayOfWeek', value as string)}
              disabled={disabled}
            />
          </BuilderRow>
        </VisualBuilder>
      ) : (
        <TextBuilder>
          <Input
            type="text"
            value={inputValue}
            onChange={handleTextChange}
            placeholder={placeholder}
            disabled={disabled}
            error={!validation.valid}
            style={{ width: '100%' }}
          />
          <HelperText>
            Format: minute hour day_of_month month_of_year day_of_week
            <br />
            Example: "0 9 * * 1,3,5" = Monday, Wednesday, Friday at 9:00 AM {timezone ? `(${formatTimezoneAbbreviation(timezone)})` : '(organization timezone)'}
          </HelperText>
        </TextBuilder>
      )}

      {!validation.valid && validation.error && (
        <ErrorText>
          <Icon name="exclamation circle" /> {validation.error}
        </ErrorText>
      )}



    </Container>
  );
};

const Container = styled.div`
  width: 100%;
`;

const ViewModeToggle = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
`;

const ToggleButton = styled.button<{ active: boolean }>`
  padding: 8px 16px;
  border: 1px solid ${props => props.active ? '#2185d0' : '#d4d4d5'};
  background: ${props => props.active ? '#2185d0' : '#fff'};
  color: ${props => props.active ? '#fff' : '#000'};
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: ${props => props.active ? '#1678c2' : '#f8f8f8'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  i.icon {
    margin: 0 !important;
  }
`;

const VisualBuilder = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background: #f9fafb;
  border: 1px solid #d4d4d5;
  border-radius: 4px;
`;

const SimpleBuilder = styled(VisualBuilder)``;

const BuilderRow = styled.div`
  display: grid;
  grid-template-columns: 140px 1fr;
  align-items: center;
  gap: 12px;
`;

const BuilderLabel = styled.label`
  font-weight: 600;
  font-size: 13px;
  color: #333;
`;

const StyledDropdown = styled(Dropdown as any)`
  width: 100%;
`;

const TextBuilder = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;


const ErrorText = styled.div`
  margin-top: 8px;
  padding: 8px 12px;
  color: #d32f2f;
  font-size: 13px;
  background: #ffebee;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 6px;

  i.icon {
    margin: 0 !important;
  }
`;

const HelperText = styled.div`
  margin-top: 8px;
  color: #666;
  font-size: 12px;
  line-height: 1.4;
`;

export default CronExpressionInput;
